import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSignup } from '../hooks/useSignUp';
import './SignUp.css';
import { ImageIcon } from 'lucide-react';

export default function SignUp() {
  const navigate = useNavigate();
  const { signup, error, isPending } = useSignup();
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
    profileImage: null,
  });
  const [thumbnailError, setThumbnailError] = useState(null);

  const handleFileChange = (e) => {
    setThumbnailError(null);
    let selected = e.target.files[0];

    if (!selected) {
      setThumbnailError('Please select a file');
      return;
    }
    if (!selected.type.includes('image')) {
      setThumbnailError('Selected file must be an image');
      return;
    }
    if (selected.size > 10000000) {
      setThumbnailError('Image file size must be less than 1MB');
      return;
    }

    setThumbnailPreview(URL.createObjectURL(selected));
    setFormData({
      ...formData,
      profileImage: selected,
    });
  };

  const handleSignup = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password || !formData.confirmPassword || !formData.displayName || !formData.profileImage) {
      alert('Please fill all the fields and upload a valid profile picture.');
      return;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(formData.email)) {
      alert('Please enter a valid email address.');
      return;
    }

    if (formData.password.length < 6) {
      alert('Password must be at least 6 characters long.');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      alert('Passwords do not match.');
      return;
    }

    if (thumbnailError) {
      alert('Please resolve the profile image issue before submitting.');
      return;
    }

    try {
      await signup(
        formData.email,
        formData.password,
        formData.displayName,
        formData.profileImage
      );
      navigate('/myaccount');
    } catch (err) {
      // Error is handled by the useSignup hook
    }
  };

  return (
    <div className="signup-container">
      <div className="signup-box">
        <div>
          <h2>Create your account</h2>
          <p>
            Or{' '}
            <Link to="/login" className="signup-link">
              sign in to your account
            </Link>
          </p>
        </div>
        {error && (
          <div className="error-box">
            <p>{error}</p>
          </div>
        )}

        <form className="signup-form" onSubmit={handleSignup}>
          <label className="profile-image-upload">
            <span>Profile picture:</span>
            <input
              type="file"
              onChange={handleFileChange}
              accept="image/*"
              style={{ display: 'none' }}
            />
            <div className="image-upload-preview">
              {thumbnailPreview ? (
                <img
                  src={thumbnailPreview}
                  alt="Profile Preview"
                  className="profile-preview"
                />
              ) : (
                <span>
                  <ImageIcon className="add-image-icon" size={80} />
                </span>
              )}
            </div>
            {thumbnailError && <div className="error">{thumbnailError}</div>}
          </label>
          <input
            type="text"
            placeholder="Full name"
            value={formData.displayName}
            onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
            required
          />
          <input
            type="email"
            placeholder="Email address"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
          />
          <input
            type="password"
            placeholder="Password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            required
          />
          <input
            type="password"
            placeholder="Confirm Password"
            value={formData.confirmPassword}
            onChange={(e) =>
              setFormData({ ...formData, confirmPassword: e.target.value })
            }
            required
          />
          <button type="submit" className="btn" disabled={isPending}>
            {isPending ? 'Signing up...' : 'Sign up'}
          </button>
        </form>
      </div>
    </div>
  );
}